// CSR-2062 — what the "Generate ACORD Form" modal must ask for, and whether it
// has enough to generate.
//
// Every rule here is READ from the shared registry (`@shared/acord-forms`,
// CSR-2055); none is restated per form. That is the point of the ticket: the
// Lodestar prototype hardcoded one form-to-behaviour table, got it wrong, and
// showed a multi-policy select on a W-9. Adding a form, or changing how an
// existing one behaves, is a registry edit — this module and the modal do not
// change.
import {
  acordPolicyMismatches,
  isAcordUmbrellaPolicyType,
  type AcordExtraField,
  type AcordFormDefinition,
  type AcordPolicyMismatch,
  type AcordPolicyTerms,
} from "@shared/acord-forms";

export type AcordExtraValues = Partial<Record<AcordExtraField["key"], string>>;

export interface AcordSelection {
  form: AcordFormDefinition | null;
  // All of the insured's policies, any status. An insured with none is the case
  // the matrix treats separately, so it is a first-class input here.
  policyCount: number;
  selectedPolicyIds: readonly string[];
  extras: AcordExtraValues;
}

export interface AcordSelectionRequirements {
  // Render a policy picker at all? `policyMode` says which control:
  // "single" → radio buttons, "multi" → checkboxes.
  asksForPolicies: boolean;
  policyMode: AcordFormDefinition["policySelection"];
  // The date inputs to render, straight from the registry — never a local list.
  extraFields: readonly AcordExtraField[];
  // Required-but-unanswered, so the modal can point at the offending field
  // instead of only greying out Generate.
  missingPolicy: boolean;
  missingExtraKeys: readonly AcordExtraField["key"][];
  canGenerate: boolean;
}

const NOTHING_SELECTED: AcordSelectionRequirements = {
  asksForPolicies: false,
  policyMode: "none",
  extraFields: [],
  missingPolicy: false,
  missingExtraKeys: [],
  canGenerate: false,
};

export function resolveAcordSelectionRequirements(
  selection: AcordSelection,
): AcordSelectionRequirements {
  const { form, policyCount, selectedPolicyIds, extras } = selection;
  if (!form) return NOTHING_SELECTED;

  // An insured with no policies is generated from insured data alone: the design
  // spec leaves the form's policy blanks empty for the agent to complete on the
  // paper, so nothing about a policy is asked for — not the picker, and not the
  // cancellation date that describes one. `policyRequired` therefore only bites
  // when there is actually something to choose from.
  const asksForPolicies = form.policySelection !== "none" && policyCount > 0;
  const asksForExtras = form.policySelection === "none" || asksForPolicies;
  // ACORD 36's `maxPolicies` is deliberately NOT enforced here. CSR-2059 owns
  // what to surface when more policies are selected than the paper has slots
  // for, and the server leaves the cap alone for the same reason.
  const extraFields = asksForExtras ? form.extraFields : [];

  const missingPolicy = form.policyRequired && asksForPolicies && selectedPolicyIds.length === 0;
  // A field the registry declares for a form is a value that form needs — the
  // matrix disables Generate for a missing cancellation date on both ACORD 35
  // and 37 — so every field asked for is required.
  const missingExtraKeys = extraFields
    .filter((field) => !extras[field.key]?.trim())
    .map((field) => field.key);

  return {
    asksForPolicies,
    policyMode: form.policySelection,
    extraFields,
    missingPolicy,
    missingExtraKeys,
    canGenerate: !missingPolicy && missingExtraKeys.length === 0,
  };
}

/**
 * What is still outstanding, in words, or null when nothing is.
 *
 * The matrix disables Generate until a form's requirements are met, so the modal
 * cannot wait for a click to say what is missing — it names the outstanding field
 * up front instead of leaving a greyed-out button to be puzzled over.
 */
export function acordPendingRequirementsMessage(
  selection: AcordSelection,
  requirements: AcordSelectionRequirements,
): string | null {
  if (!selection.form) return "Select a form to continue.";
  const outstanding = [
    ...(requirements.missingPolicy ? ["a policy"] : []),
    ...requirements.extraFields
      .filter((field) => requirements.missingExtraKeys.includes(field.key))
      .map((field) => `the ${field.label}`),
  ];
  if (outstanding.length === 0) return null;
  const list =
    outstanding.length === 1
      ? outstanding[0]
      : `${outstanding.slice(0, -1).join(", ")} and ${outstanding[outstanding.length - 1]}`;
  return `Select ${list} to continue.`;
}

// A policy as the picker lists it — the insured's policy row carries both terms.
export interface AcordPolicyOption extends AcordPolicyTerms {
  id: string;
  type: string | null;
}

const MISMATCH_PHRASES: Record<
  AcordPolicyMismatch["field"],
  [differs: string, notOnRecord: string]
> = {
  carrier: ["different carrier", "no carrier on record"],
  effectiveDate: ["different effective date", "no effective date on record"],
};

/**
 * CSR-2331 — why `policy` cannot join the current selection, in a few words, or
 * null when it can.
 *
 * Only a form that covers one carrier and one effective date restricts anything
 * (ACORD 131). Once a policy is picked, every other option is compared with it
 * by the same `acordPolicyMismatches` the server refuses a request with, so an
 * option the modal leaves enabled is one the server accepts. With nothing picked
 * every option is available — deselecting the last policy re-opens the list.
 */
export function acordPolicyUnavailableReason(
  form: AcordFormDefinition | null,
  policy: AcordPolicyOption,
  selectedPolicies: readonly AcordPolicyOption[],
): string | null {
  if (!form?.policiesMustShareCarrierAndEffectiveDate) return null;
  if (selectedPolicies.some((selected) => selected.id === policy.id)) return null;
  // The umbrella is exempt — it fills the header, not an underlying row — so it
  // is never disabled and never decides which other policies may join.
  if (isAcordUmbrellaPolicyType(policy.type)) return null;
  // Every selected underlying policy already matches the first, so the first
  // speaks for all.
  const anchor = selectedPolicies.find((selected) => !isAcordUmbrellaPolicyType(selected.type));
  if (!anchor) return null;

  const mismatches = acordPolicyMismatches(anchor, policy);
  if (mismatches.length === 0) return null;
  const reason = mismatches
    .map(({ field, notOnRecord }) => MISMATCH_PHRASES[field][notOnRecord ? 1 : 0])
    .join(" and ");
  return reason.charAt(0).toUpperCase() + reason.slice(1);
}

/**
 * The line under the picker that says what to do about the disabled options.
 * Worded from the form's own document name rather than a hardcoded "ACORD 131",
 * so it stays right for any form the registry gives the same rule.
 */
export function acordPolicyRestrictionHint(form: AcordFormDefinition): string {
  return (
    `${form.documentName} covers one carrier and one effective date. ` +
    "Generate a separate form for the other policies."
  );
}

/**
 * The part of a carried-over selection a form can hold when the agent switches
 * to it — the first policy, and every later one that may share a form with it.
 * The same walk as clicking them in order: a policy the modal would disable is
 * not kept. Policies missing from `policies` cannot be checked, so they are not
 * kept either; the server would refuse them as not this insured's anyway.
 */
export function acordSelectionCoveredByForm(
  form: AcordFormDefinition,
  selectedPolicyIds: readonly string[],
  policies: readonly AcordPolicyOption[],
): string[] {
  if (!form.policiesMustShareCarrierAndEffectiveDate) return [...selectedPolicyIds];
  const kept: AcordPolicyOption[] = [];
  for (const id of selectedPolicyIds) {
    const policy = policies.find((candidate) => candidate.id === id);
    if (policy && acordPolicyUnavailableReason(form, policy, kept) === null) kept.push(policy);
  }
  return kept.map((policy) => policy.id);
}

// "SELECT A POLICY" vs "SELECT POLICY(IES)" — the heading follows the mode, so it
// cannot contradict the control underneath it.
export function acordPolicySectionLabel(
  policyMode: AcordFormDefinition["policySelection"],
): string {
  return policyMode === "single" ? "SELECT A POLICY" : "SELECT POLICY(IES)";
}

export function acordPolicySectionHint(
  policyMode: AcordFormDefinition["policySelection"],
  policyRequired: boolean,
): string {
  if (policyMode === "single") return policyRequired ? "(exactly one)" : "(one)";
  return policyRequired ? "(one or more)" : "(optional)";
}
